/**
 * IndexedDB utility for chat storage.
 * Provides a simple promise-based API for storing and retrieving conversations, messages,
 * and tracking invited persons per device.
 */

const DB_NAME = 'Dare2ChatDB';
const DB_VERSION = 4;
const CONVERSATIONS_STORE = 'conversations';
const MESSAGES_STORE = 'messages';
const INVITED_PERSONS_STORE = 'invitedPersons';

/**
 * Opens (or creates) the IndexedDB database and ensures object stores exist.
 * @returns {Promise<IDBDatabase>}
 */
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Conversations store: keyed by conversation id
      if (!db.objectStoreNames.contains(CONVERSATIONS_STORE)) {
        const store = db.createObjectStore(CONVERSATIONS_STORE, { keyPath: 'id' });
        store.createIndex('userId', 'userId', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
        store.createIndex('inviteCode', 'inviteCode', { unique: false });
        store.createIndex('chatType', 'chatType', { unique: false });
      } else {
        // Upgrade existing store: add missing indexes
        const store = event.target.transaction.objectStore(CONVERSATIONS_STORE);
        if (!store.indexNames.contains('inviteCode')) {
          store.createIndex('inviteCode', 'inviteCode', { unique: false });
        }
        if (!store.indexNames.contains('chatType')) {
          store.createIndex('chatType', 'chatType', { unique: false });
        }
        // Add unreadCount and chatType fields to existing conversations
        const cursorRequest = store.openCursor();
        cursorRequest.onsuccess = (e) => {
          const cursor = e.target.result;
          if (cursor) {
            const conversation = cursor.value;
            let needsUpdate = false;
            if (conversation.unreadCount === undefined) {
              conversation.unreadCount = 0;
              needsUpdate = true;
            }
            if (conversation.chatType === undefined) {
              conversation.chatType = conversation.isInvite ? 'anonymous' : 'my_chat';
              needsUpdate = true;
            }
            if (needsUpdate) {
              cursor.update(conversation);
            }
            cursor.continue();
          }
        };
      }

      // Messages store: keyed by message id, with an index on conversationId
      if (!db.objectStoreNames.contains(MESSAGES_STORE)) {
        const store = db.createObjectStore(MESSAGES_STORE, { keyPath: 'id' });
        store.createIndex('conversationId', 'conversationId', { unique: false });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Invited persons store: keyed by invite code, tracks which invites this device has used
      if (!db.objectStoreNames.contains(INVITED_PERSONS_STORE)) {
        const store = db.createObjectStore(INVITED_PERSONS_STORE, { keyPath: 'inviteCode' });
        store.createIndex('conversationId', 'conversationId', { unique: false });
        store.createIndex('joinedAt', 'joinedAt', { unique: false });
      }
    };

    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
}

/**
 * Generates a simple unique ID.
 * @returns {string}
 */
function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

// ─── Conversation Operations ─────────────────────────────────────────────────

/**
 * Retrieves all conversations for a given user, sorted by most recent first.
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export async function getConversations(userId) {
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readonly');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  const index = store.index('userId');
  const conversations = await new Promise((resolve, reject) => {
    const results = [];
    const request = index.openCursor(IDBKeyRange.only(userId), 'prev');

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        results.push(cursor.value);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    request.onerror = () => reject(request.error);
  });

  // Sort by updatedAt descending (most recent first)
  conversations.sort((a, b) => b.updatedAt - a.updatedAt);

  db.close();
  return conversations;
}

/**
 * Retrieves a single conversation by its id.
 * @param {string} conversationId
 * @returns {Promise<Object|null>}
 */
export async function getConversation(conversationId) {
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readonly');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  const conversation = await new Promise((resolve, reject) => {
    const request = store.get(conversationId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return conversation;
}

/**
 * Saves (inserts or updates) a conversation.
 * Ensures unreadCount and chatType fields are present.
 * @param {Object} conversation - { id, name, lastMessage, avatar, userId, updatedAt }
 * @returns {Promise<void>}
 */
export async function saveConversation(conversation) {
  // Ensure unreadCount and chatType defaults
  if (conversation.unreadCount === undefined) {
    conversation.unreadCount = 0;
  }
  if (conversation.chatType === undefined) {
    conversation.chatType = conversation.isInvite ? 'anonymous' : 'my_chat';
  }
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readwrite');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  await new Promise((resolve, reject) => {
    const request = store.put(conversation);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
  db.close();
}

/**
 * Creates a new conversation and returns it.
 * @param {Object} params
 * @param {string} params.name - Display name of the other participant
 * @param {string} params.userId - The current user's ID
 * @param {string} [params.avatar] - Avatar URL
 * @returns {Promise<Object>} The created conversation
 */
export async function createConversation({ name, userId, avatar }) {
  const conversation = {
    id: generateId(),
    name,
    userId,
    avatar: avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1e293b&color=fff`,
    lastMessage: '',
    updatedAt: Date.now(),
  };
  await saveConversation(conversation);
  return conversation;
}

/**
 * Retrieves all conversations that have an inviteCode (guest conversations).
 * These are conversations created when a guest joins an invite link.
 * @param {string} [excludeUserId] - If provided, conversations owned by this user are excluded.
 * @returns {Promise<Array>}
 */
export async function getGuestConversations(excludeUserId) {
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readonly');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  const conversations = await new Promise((resolve, reject) => {
    const results = [];
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const conv = cursor.value;
        // Only include conversations that are explicitly anonymous/guest chats.
        // This prevents my_chat conversations with isInvite: true from leaking
        // into the guest conversation list.
        if (conv.chatType !== 'my_chat' && (conv.isInvite || conv.inviteCode)) {
          // Exclude conversations owned by the specified user (e.g., logged-in user's own invites)
          if (!excludeUserId || conv.userId !== excludeUserId) {
            results.push(conv);
          }
        }
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    request.onerror = () => reject(request.error);
  });

  // Sort by updatedAt descending (most recent first)
  conversations.sort((a, b) => b.updatedAt - a.updatedAt);

  db.close();
  return conversations;
}

/**
 * Deletes a conversation and all its messages.
 * @param {string} conversationId
 * @returns {Promise<void>}
 */
export async function deleteConversation(conversationId) {
  const db = await openDB();
  
  // Delete the conversation
  const tx1 = db.transaction(CONVERSATIONS_STORE, 'readwrite');
  const store1 = tx1.objectStore(CONVERSATIONS_STORE);
  await new Promise((resolve, reject) => {
    const request = store1.delete(conversationId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });

  // Delete all messages for this conversation
  const tx2 = db.transaction(MESSAGES_STORE, 'readwrite');
  const store2 = tx2.objectStore(MESSAGES_STORE);
  const index = store2.index('conversationId');
  await new Promise((resolve, reject) => {
    const request = index.openCursor(IDBKeyRange.only(conversationId));
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = () => reject(request.error);
  });

  db.close();
}

// ─── Message Operations ──────────────────────────────────────────────────────

/**
 * Retrieves all messages for a given conversation, sorted by timestamp ascending.
 * @param {string} conversationId
 * @returns {Promise<Array>}
 */
export async function getMessages(conversationId) {
  const db = await openDB();
  const tx = db.transaction(MESSAGES_STORE, 'readonly');
  const store = tx.objectStore(MESSAGES_STORE);
  const index = store.index('conversationId');
  const messages = await new Promise((resolve, reject) => {
    const results = [];
    const request = index.openCursor(IDBKeyRange.only(conversationId));

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        results.push(cursor.value);
        cursor.continue();
      } else {
        resolve(results);
      }
    };
    request.onerror = () => reject(request.error);
  });

  // Sort by timestamp ascending
  messages.sort((a, b) => a.timestamp - b.timestamp);

  db.close();
  return messages;
}

/**
 * Saves a single message to IndexedDB.
 * Also updates the parent conversation's lastMessage and updatedAt.
 * @param {Object} params
 * @param {string} params.conversationId
 * @param {string} params.text
 * @param {string} params.sender - 'me' or 'them'
 * @param {number} [params.timestamp] - Defaults to Date.now()
 * @returns {Promise<Object>} The saved message
 */
export async function saveMessage({ conversationId, text, sender, timestamp }) {
  const msg = {
    id: generateId(),
    conversationId,
    text,
    sender,
    timestamp: timestamp || Date.now(),
  };

  const db = await openDB();

  // Save the message
  const tx1 = db.transaction(MESSAGES_STORE, 'readwrite');
  const store1 = tx1.objectStore(MESSAGES_STORE);
  await new Promise((resolve, reject) => {
    const request = store1.put(msg);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });

  // Update the conversation's lastMessage and updatedAt
  const tx2 = db.transaction(CONVERSATIONS_STORE, 'readwrite');
  const store2 = tx2.objectStore(CONVERSATIONS_STORE);
  await new Promise((resolve, reject) => {
    const request = store2.get(conversationId);
    request.onsuccess = (event) => {
      const conversation = event.target.result;
      if (conversation) {
        conversation.lastMessage = text;
        conversation.updatedAt = Date.now();
        store2.put(conversation);
      }
      resolve();
    };
    request.onerror = () => reject(request.error);
  });

  db.close();
  return msg;
}

/**
 * Saves multiple messages in batch (e.g., when loading from a remote source).
 * @param {Array} messages - Array of message objects with conversationId, text, sender, timestamp
 * @returns {Promise<void>}
 */
export async function saveMessages(messages) {
  const db = await openDB();
  const tx = db.transaction(MESSAGES_STORE, 'readwrite');
  const store = tx.objectStore(MESSAGES_STORE);
  
  for (const msg of messages) {
    store.put({
      id: msg.id || generateId(),
      conversationId: msg.conversationId,
      text: msg.text,
      sender: msg.sender,
      timestamp: msg.timestamp || Date.now(),
    });
  }

  await new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  db.close();
}

// ─── Unread Count Operations ─────────────────────────────────────────────────

/**
 * Increments the unread count for a conversation by 1.
 * @param {string} conversationId
 * @returns {Promise<void>}
 */
export async function incrementUnreadCount(conversationId) {
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readwrite');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  await new Promise((resolve, reject) => {
    const request = store.get(conversationId);
    request.onsuccess = (event) => {
      const conversation = event.target.result;
      if (conversation) {
        conversation.unreadCount = (conversation.unreadCount || 0) + 1;
        store.put(conversation);
      }
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
  db.close();
}

/**
 * Resets the unread count for a conversation to 0.
 * @param {string} conversationId
 * @returns {Promise<void>}
 */
export async function resetUnreadCount(conversationId) {
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readwrite');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  await new Promise((resolve, reject) => {
    const request = store.get(conversationId);
    request.onsuccess = (event) => {
      const conversation = event.target.result;
      if (conversation) {
        conversation.unreadCount = 0;
        store.put(conversation);
      }
      resolve();
    };
    request.onerror = () => reject(request.error);
  });
  db.close();
}

/**
 * Gets the total unread count for all conversations of a given type.
 * @param {string} userId - The current user's ID
 * @param {'my_chat'|'anonymous'} chatType - The type of conversations to count
 * @returns {Promise<number>} Total unread count
 */
export async function getUnreadCountByType(userId, chatType) {
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readonly');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  const index = store.index('userId');
  const count = await new Promise((resolve, reject) => {
    let total = 0;
    const request = index.openCursor(IDBKeyRange.only(userId));
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const conv = cursor.value;
        if (conv.chatType === chatType || (chatType === 'my_chat' && !conv.chatType && !conv.isInvite)) {
          total += conv.unreadCount || 0;
        }
        cursor.continue();
      } else {
        resolve(total);
      }
    };
    request.onerror = () => reject(request.error);
  });
  db.close();
  return count;
}

/**
 * Gets the total unread count for guest conversations (anonymous chats)
 * belonging to a specific user.
 * @param {string} userId - The user's ID to scope the count to
 * @returns {Promise<number>} Total unread count
 */
export async function getGuestUnreadCount(userId) {
  const db = await openDB();
  const tx = db.transaction(CONVERSATIONS_STORE, 'readonly');
  const store = tx.objectStore(CONVERSATIONS_STORE);
  const index = store.index('userId');
  const count = await new Promise((resolve, reject) => {
    let total = 0;
    const request = index.openCursor(IDBKeyRange.only(userId));
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const conv = cursor.value;
        // Count as guest/anonymous only if chatType is NOT explicitly 'my_chat'
        // This prevents my_chat conversations with isInvite: true from leaking into the anonymous tab
        if (conv.chatType !== 'my_chat' && (conv.isInvite || conv.inviteCode || conv.chatType === 'anonymous')) {
          total += conv.unreadCount || 0;
        }
        cursor.continue();
      } else {
        resolve(total);
      }
    };
    request.onerror = () => reject(request.error);
  });
  db.close();
  return count;
}

// ─── Invited Persons Operations ──────────────────────────────────────────────

/**
 * Records that this device has joined an invite.
 * This prevents the same device from creating duplicate chat rooms for the same invite.
 * @param {Object} params
 * @param {string} params.inviteCode - The invite code
 * @param {string} params.conversationId - The conversation ID created from this invite
 * @param {string} params.participantName - The name used to join
 * @param {string} params.participantId - The participant ID assigned
 * @param {string} params.hostName - The name of the person who created the invite
 * @returns {Promise<void>}
 */
export async function recordInvitedPerson({ inviteCode, conversationId, participantName, participantId, hostName }) {
  const db = await openDB();
  const tx = db.transaction(INVITED_PERSONS_STORE, 'readwrite');
  const store = tx.objectStore(INVITED_PERSONS_STORE);
  
  await new Promise((resolve, reject) => {
    const request = store.put({
      inviteCode,
      conversationId,
      participantName,
      participantId,
      hostName,
      joinedAt: Date.now(),
    });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });

  db.close();
}

/**
 * Checks if this device has already joined a given invite.
 * @param {string} inviteCode
 * @returns {Promise<Object|null>} The invited person record, or null if not found
 */
export async function getInvitedPerson(inviteCode) {
  const db = await openDB();
  const tx = db.transaction(INVITED_PERSONS_STORE, 'readonly');
  const store = tx.objectStore(INVITED_PERSONS_STORE);
  
  const record = await new Promise((resolve, reject) => {
    const request = store.get(inviteCode);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });

  db.close();
  return record;
}

/**
 * Gets all invites this device has joined.
 * @returns {Promise<Array>} Array of invited person records
 */
export async function getAllInvitedPersons() {
  const db = await openDB();
  const tx = db.transaction(INVITED_PERSONS_STORE, 'readonly');
  const store = tx.objectStore(INVITED_PERSONS_STORE);
  
  const records = await new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });

  db.close();
  return records;
}

/**
 * Removes an invited person record (e.g., when the invite is deactivated).
 * @param {string} inviteCode
 * @returns {Promise<void>}
 */
export async function removeInvitedPerson(inviteCode) {
  const db = await openDB();
  const tx = db.transaction(INVITED_PERSONS_STORE, 'readwrite');
  const store = tx.objectStore(INVITED_PERSONS_STORE);
  
  await new Promise((resolve, reject) => {
    const request = store.delete(inviteCode);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });

  db.close();
}
